import lodash from 'lodash'

import * as projectsApi from '@/api/projects/request'
import i18n from '@/utils/i18n'
import navigation from '@/utils/navigation'
import types from '@/store/mutation-types'
import { aclHelper } from '@/utils/aclHelper'
import { submissionStatus } from '@/utils/locale'
import { updateVueObject } from '@/store/util'
import { getPurchase } from '@/api/projects/util'

const defaultState = {
  currentProject: {},
  lastSearchCount: 0,
  lastSearchedAttributes: {},
  allLastSearchedProjects: [],
}
const getters = {
  currentProject: state => state.currentProject,
  lastSearchCount: state => state.lastSearchCount,
  lastSearchedAttributes: state => state.lastSearchedAttributes,
  allLastSearchedProjects: state => state.allLastSearchedProjects,
  currentProjectHistory: state => state.currentProject.histories,
  wpInUsedBuildingIds: state => state.currentProject.wpInUsedBuildingIds,
  hasRetention: state => {
    return (deep(state.currentProject, 'retention.percentValue') || 0) > 0
  },
  purchase: state => {
    let result = {
      amount: 0,
      siteId: '',
    }
    if (state.currentProject.numberType) {
      result = getPurchase(state.currentProject, result)
    }
    return result
  },
  currentSubmissions: state => {
    const submissions = lodash.get(state.currentProject, 'submissions', [])
    return submissions
  },
  typeLabel: state => {
    return state.currentProject.numberType ? i18n.t(`project.type.${state.currentProject.numberType.toLowerCase()}`) : ''
  },
  hasCurrentProjectAccess (state, getters, rootState, rootGetters) {
    const currentUser = rootGetters['user/currentUser'] || {}
    const isContractor = rootGetters['user/isContractor']
    const isMainUser = rootGetters['projectContractors/isMainUser']
    const currentProject = state.currentProject
    if (isContractor) {
      const isVendor = currentUser.externalId === currentProject.vendorId
      return isMainUser || (isVendor && aclHelper.hasProjectsAccess(true))
    }
    const locations = currentUser.locations || []
    const hasSiteAccess = locations.some(site => site.code === getPurchase(state.currentProject).siteId)
    return hasSiteAccess && aclHelper.hasProjectsAccess()
  },
}

function updateSrAmount (project) {
  const submissions = lodash.get(project, 'submissions')
  if (!Array.isArray(submissions)) return

  submissions.forEach(submission => {
    if (![submissionStatus.DRAFT, submissionStatus.REJECTED].includes(submission.status)) return

    const purchase = getPurchase(project)
    const poAmount = deep(purchase, 'amount') || 0
    const retentionAmountPct = deep(project, 'retention.percentValue') || 0
    submission.deductionAmount = deep(project, 'deduction.amount') || 0
    submission.retentionAmount = retentionAmountPct * (poAmount - submission.deductionAmount) / 100
  })
}

async function getProject (projectId) {
  if (!projectId) {
    throw new Error({ message: `invalid param: ${projectId}` })
  }
  const project = await projectsApi.getProject(projectId)
  updateSrAmount(project)
  return project
}

const actions = {
  async createProject ({ commit }, { purchaseOrder, contract, poAttachmentId, navToPage, tenderId }) {
    const project = await projectsApi.createProject({ purchaseOrder, contract, poAttachmentId, tenderId })
    if (!project) return
    commit(types.PROJECT_UPDATE_CURRENT, { project })
    if (navToPage) {
      navigation.toPage(navToPage, { id: project.id })
    }
  },

  async updateProject ({ state, commit }, { projectId, request }) {
    await projectsApi.updateProject(projectId, request)
  },
  async updateAndRefreshProject ({ dispatch }, { projectId, request }) {
    await projectsApi.updateProject(projectId, request)
    await dispatch('loadProject', projectId)
  },
  async updateProjectStatus ({ state, commit }, { id, status, action }) {
    await projectsApi.updateProjectStatus(id, status, action)
  },

  async createSubmission ({ state }, projectId) {
    await projectsApi.createSubmission(projectId)
  },

  async saveSubmission ({ state }, { submission, showNotification }) {
    await projectsApi.updateSubmission(submission, showNotification)
  },

  async updateSubmissionStatus (_, { id, status, actionHistory, action, ...restValues }) {
    await projectsApi.updateSubmissionStatus(id, status, actionHistory, action, restValues)
  },

  async removeSubmission ({ state }, id) {
    await projectsApi.removeSubmission(id)
  },

  async loadProject ({ commit, dispatch }, projectId) {
    const project = await getProject(projectId)
    if (lodash.get(project, 'workPermits.length')) {
      dispatch('workPermit/setWorkPermitsForTenantProject', { permits: project.workPermits, needRoot: true }, { root: true })
    }
    dispatch('projectContractors/setContractors', { project, needRoot: true, isTms: false }, { root: true })
    dispatch('projectContractors/getVendorUserList', project.vendorId, {root: true})
    commit(types.PROJECT_UPDATE_CURRENT, { project })
    return project
  },
  setLastSearchedAttributes ({ commit }, lastSearchedAttributes) {
    const attributes = lodash.cloneDeep(lastSearchedAttributes)
    commit(types.SET_LAST_SEARCH_ATTRIBUTES, { lastSearchedAttributes: attributes })
  },
  async loadAllSearchedProjects ({ commit }, { parsedAttributes, diskCacheKey }) {
    const [allLastSearchedProjects, lastSearchCount] = await projectsApi.listAllProjects(parsedAttributes, diskCacheKey)
    commit(types.PROJECT_LOAD_ALL_LAST_SEARCHED, { allLastSearchedProjects, lastSearchCount })
  },
  async setProjects ({ commit }, list) {
    commit(types.PROJECT_LOAD_ALL_LAST_SEARCHED, { allLastSearchedProjects: list, lastSearchCount: list.length })
  },
}

const mutations = {
  [types.PROJECT_UPDATE_CURRENT] (state, { project }) {
    state.currentProject = project
  },
  [types.UPDATE_PATH_FIELD] (state, { path, newValue }) {
    updateVueObject(state.currentProject, path, newValue)
  },
  [types.RESET_STATE] (state) {
    state.currentProject = {}
    state.lastSearchedAttributes = null
    state.allLastSearchedProjects = []
  },
  [types.SET_LAST_SEARCH_ATTRIBUTES] (state, {lastSearchedAttributes}) {
    state.lastSearchedAttributes = lastSearchedAttributes
  },
  [types.PROJECT_LOAD_ALL_LAST_SEARCHED] (state, { allLastSearchedProjects, lastSearchCount }) {
    state.allLastSearchedProjects = allLastSearchedProjects
    state.lastSearchCount = lastSearchCount
  },
}

export default {
  namespaced: true,
  state: defaultState,
  getters,
  actions,
  mutations,
}
